import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";

export interface AwardGivingBudgetResponse {
  user_id: number;
  balance: number;
  max_cap: number;
  weekly_allowance: number;
  trust_tier: string;
  last_allowance_granted_at?: string | null;
}

export interface ReputationResponse {
  actor_type: string;
  actor_id: number;
  awards_received_count: number;
  trusted_award_count: number;
  unique_awarder_count: number;
  first_award_at?: string | null;
  last_award_at?: string | null;
}

function parsePositiveInteger(value: unknown, fieldName: string): number {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`Invalid ${fieldName} in award balance response.`);
  }
  return parsed;
}

function parseNonNegativeInteger(value: unknown, fieldName: string): number {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`Invalid ${fieldName} in award balance response.`);
  }
  return parsed;
}

export const awardsApi = {
  getBudget: async (): Promise<AwardGivingBudgetResponse> => {
    const response = await api.get<AwardGivingBudgetResponse>(apiRoutes.awards.budget);

    return {
      user_id: parsePositiveInteger(response.data?.user_id, "user_id"),
      balance: parseNonNegativeInteger(response.data?.balance, "balance"),
      max_cap: parseNonNegativeInteger(response.data?.max_cap ?? 0, "max_cap"),
      weekly_allowance: parseNonNegativeInteger(response.data?.weekly_allowance ?? 0, "weekly_allowance"),
      trust_tier: String(response.data?.trust_tier || "pre_verified"),
      last_allowance_granted_at: response.data?.last_allowance_granted_at ?? null,
    };
  },
  getBalance: async (): Promise<AwardGivingBudgetResponse> => awardsApi.getBudget(),
  getReputation: async (actorType: string, actorId: number | string): Promise<ReputationResponse> => {
    const response = await api.get<ReputationResponse>(apiRoutes.awards.reputation(actorType, actorId));
    return {
      actor_type: String(response.data?.actor_type || actorType),
      actor_id: parsePositiveInteger(response.data?.actor_id ?? actorId, "actor_id"),
      awards_received_count: parseNonNegativeInteger(response.data?.awards_received_count ?? 0, "awards_received_count"),
      trusted_award_count: parseNonNegativeInteger(response.data?.trusted_award_count ?? 0, "trusted_award_count"),
      unique_awarder_count: parseNonNegativeInteger(response.data?.unique_awarder_count ?? 0, "unique_awarder_count"),
      first_award_at: response.data?.first_award_at ?? null,
      last_award_at: response.data?.last_award_at ?? null,
    };
  },
};
